// @ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import { PathNames } from 'constants/General'
import { usePendingAgreementsContext } from 'hooks/context/usePendingAgreementsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { usePendingAgreementsQuery } from 'hooks/queries/usePendingAgreementsQuery'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { LegalAgreementType } from 'pages/LegalAgreements/types'
import { getIsAgreementAssignedToUser } from 'pages/LegalAgreements/utils'
import { AgreementModalBody } from './AgreementModalBody'
import { AgreementModalData } from './types'
import { useAgreementsModalData } from './useAgreementsModalData'
import { filterSingleAgreement, orderAgreements } from './utils'

export const PendingAgreements = () => {
  const location = useLocation()
  const { profile } = useProfileContext()
  const currentpath = location.pathname
  const excludedPaths = [PathNames.NOT_FOUND]
  const isExcludedPath = excludedPaths.includes(currentpath)

  if (isExcludedPath) return <></>

  const permissions = useMappedRoles()
  const hasLegalAgreementsRolePermissions = permissions?.manageLegalAgreements?.canEdit ?? false

  const [pendingAgreements, setPendingAgreements] = useState<AgreementModalData[]>([])
  const [hasMappedAgreements, setHasMappedAgreements] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const {
    isFetching: isFetchingAgreementsData,
    data: agreementsData,
    refetch: refetchPendingAgreements,
  } = usePendingAgreementsQuery(hasLegalAgreementsRolePermissions)

  const hasAgreements = useMemo(() => !!agreementsData?.length, [agreementsData])

  const { showSingleAgreementId, shouldRefetch, setShouldRefetch } = usePendingAgreementsContext()
  const { getBodyContent, getLinks, getCheckboxLabel } = useAgreementsModalData()

  const handleCloseModal = () => setIsModalOpen(false)

  const mapAgreements = () => {
    const userAgreements = agreementsData?.filter((agreement: LegalAgreementType) =>
      getIsAgreementAssignedToUser(agreement, profile)
    )
    const agreementsToShow: LegalAgreementType[] = !!showSingleAgreementId
      ? filterSingleAgreement(showSingleAgreementId, userAgreements)
      : orderAgreements('EMI', userAgreements)

    const mappedAgreements: AgreementModalData[] = agreementsToShow?.map(
      ({ id: agreementId, agreement, appendixes }: LegalAgreementType) => {
        const { name, displayName, description, declinable: isDeclinable } = agreement || {}
        const links = getLinks(agreement, appendixes)
        return {
          agreementId,
          title: displayName,
          bodyContent: getBodyContent(name, displayName, description),
          links,
          checkboxLabel: getCheckboxLabel(name, links),
          status: 'PENDING',
          isDeclinable,
        }
      }
    )

    setPendingAgreements(mappedAgreements)
    setHasMappedAgreements(true)
    setShouldRefetch(false)
    if (!isModalOpen) {
      setIsModalOpen(true)
    }
  }

  useEffect(() => {
    if (shouldRefetch) {
      const singleAgreementIndex = agreementsData?.findIndex(
        (agreementData: LegalAgreementType) => agreementData?.id === showSingleAgreementId
      )
      if (singleAgreementIndex !== -1) {
        mapAgreements()
      } else {
        refetchPendingAgreements()
        setIsModalOpen(true)
      }
      setHasMappedAgreements(false)
    }
  }, [shouldRefetch])

  useEffect(() => {
    if (
      hasLegalAgreementsRolePermissions &&
      !isFetchingAgreementsData &&
      hasAgreements &&
      !hasMappedAgreements
    ) {
      mapAgreements()
    }
  }, [
    hasLegalAgreementsRolePermissions,
    isFetchingAgreementsData,
    hasAgreements,
    hasMappedAgreements,
  ])

  return hasLegalAgreementsRolePermissions && !!pendingAgreements?.length && isModalOpen ? (
    <AgreementModalBody
      agreements={pendingAgreements}
      isLoadingAgreements={isFetchingAgreementsData}
      onClose={handleCloseModal}
    />
  ) : (
    <></>
  )
}
